/**
 * ComposerAttachmentTray — files being staged for THIS turn.
 *
 * One line, horizontally scrolling, never wrapping — the same rail and the same
 * chip grammar as the context row directly above it (ComposerScrollRow /
 * ComposerChip), so the two read as one system and the composer's height stays
 * bounded no matter how much is staged. It used to WRAP 180px cards: five files
 * cost ~280px of panel height and pushed the thread off screen.
 *
 * File size is not on the chip. Nobody scans KB before sending, and at this
 * density it cost more room than the filename — it lives in the hover tooltip.
 *
 * Deliberately a different treatment from the context row's CONTENTS: these
 * aren't entities yet. They're consumed by the turn and become captures
 * afterwards, at which point they can appear as context chips like anything
 * else.
 */

import { Box, HStack, Image, Text } from '@chakra-ui/react';
import { useEffect, useState } from 'react';

import { MaterialIcon } from '../../ui/MaterialIcon';
import { Tooltip } from '../../ui/Tooltip';
import { ComposerChip, type ComposerChipTone } from './ComposerChip';
import { ComposerScrollRow } from './ComposerScrollRow';
import { BowerSpinner } from '../../ui/BowerSpinner';

/**
 * Staged files have no upload phase today — they ride along with the message
 * as multipart on send (see services/agentChat.service.ts), so nothing in the
 * app currently produces anything but 'ready'. The other two states exist
 * because the chip has to be able to express them WITHOUT changing size when
 * staged uploads land; a spinner that reflows the row is worse than no spinner.
 */
export type AttachmentStatus = 'ready' | 'uploading' | 'error';

const STATUS_TONE: Record<AttachmentStatus, ComposerChipTone> = {
  ready: 'default',
  uploading: 'active',
  error: 'error',
};

/** Past this many files the row collapses to a summary. */
const COLLAPSE_THRESHOLD = 6;

interface ComposerAttachmentTrayProps {
  readonly files: readonly File[];
  readonly onRemove: (index: number) => void;
  /**
   * Parallel to `files`; anything absent is treated as 'ready'.
   *
   * NOT WIRED IN PRODUCTION YET. `ChatComposer` holds attachments as a plain
   * `File[]` and posts them with the message, so there is no upload phase to
   * report on and nothing can currently pass this. The states are implemented
   * and tested because the chip must be able to express them without changing
   * size once staged uploads land — a spinner that reflows the row is worse
   * than no spinner. Treat the tests below as pinning a contract, not as
   * evidence that a user has ever seen an uploading chip.
   */
  readonly statuses?: readonly AttachmentStatus[];
  /**
   * Click-to-retry on a failed chip. Without it a failed chip is inert.
   * Unreachable today for the same reason as `statuses` above.
   */
  readonly onRetry?: (index: number) => void;
}

/** Bytes → the shortest human string that stays honest (1 decimal above KB). */
export function formatFileSize(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  const units = ['KB', 'MB', 'GB'];
  let value = bytes / 1024;
  let unitIndex = 0;
  while (value >= 1024 && unitIndex < units.length - 1) {
    value /= 1024;
    unitIndex += 1;
  }
  return `${value < 10 ? value.toFixed(1) : Math.round(value)} ${units[unitIndex]}`;
}

/**
 * Up to 3 letters from the extension — "PDF", "CSV", "DOC". Reads faster at
 * 18px than a generic document glyph, which is the same shape for every
 * non-image file. Falls back to an icon when there's no usable extension.
 */
export function fileTypeLetters(name: string): string | null {
  const dot = name.lastIndexOf('.');
  if (dot <= 0 || dot === name.length - 1) return null;
  const ext = name.slice(dot + 1).replace(/[^a-z0-9]/gi, '');
  if (!ext) return null;
  return ext.slice(0, 3).toUpperCase();
}

/**
 * Object-URL thumbnail for image files. Revoked on unmount — a composer that
 * churns attachments would otherwise leak a blob per preview.
 */
function useImagePreview(file: File): string | null {
  const [url, setUrl] = useState<string | null>(null);

  useEffect(() => {
    if (!file.type.startsWith('image/') || typeof URL.createObjectURL !== 'function') return;
    const objectUrl = URL.createObjectURL(file);
    setUrl(objectUrl);
    return () => {
      URL.revokeObjectURL(objectUrl);
      setUrl(null);
    };
  }, [file]);

  return url;
}

/** The well's contents: thumbnail, spinner, or file-type letters. */
function AttachmentWell({
  file,
  status,
  previewUrl,
}: {
  readonly file: File;
  readonly status: AttachmentStatus;
  readonly previewUrl: string | null;
}) {
  // Same well, same size — an in-flight chip must not reflow the row.
  if (status === 'uploading') return <BowerSpinner size="xs" />;
  if (previewUrl) return <Image src={previewUrl} alt="" w="18px" h="18px" objectFit="cover" />;

  const letters = fileTypeLetters(file.name);
  if (letters) {
    return (
      <Text
        fontSize="2xs"
        fontWeight="bold"
        color="text.secondary"
        // Horizontal padding, not a squeeze: the well grows to fit, so "DOC"
        // reads as a badge instead of three letters crushed into a square.
        px={1}
        lineHeight="1"
        whiteSpace="nowrap"
      >
        {letters}
      </Text>
    );
  }
  return <MaterialIcon name="draft" boxSize={3.5} color="text.muted" />;
}

function AttachmentChip({
  file,
  status,
  onRemove,
  onRetry,
}: {
  readonly file: File;
  readonly status: AttachmentStatus;
  readonly onRemove: () => void;
  readonly onRetry?: () => void;
}) {
  const previewUrl = useImagePreview(file);

  const tooltip =
    status === 'error'
      ? `${file.name}: upload failed, click to retry`
      : `${file.name}: ${formatFileSize(file.size)}`;

  return (
    <Tooltip label={tooltip} placement="top" hasArrow>
      <Box data-composer-chip flexShrink={0}>
        <ComposerChip
          well={<AttachmentWell file={file} status={status} previewUrl={previewUrl} />}
          label={file.name}
          tone={STATUS_TONE[status]}
          // Withheld until the upload settles — removing a file mid-flight
          // leaves a request nothing will ever reconcile.
          onRemove={status === 'uploading' ? undefined : onRemove}
          // Names the size in the REMOVE button's label too. The tooltip hangs
          // off a non-focusable wrapper, so on a ready chip the remove button
          // is the only tab stop — without this, keyboard and touch users have
          // no route to the size at all (WCAG 1.4.13).
          removeLabel={`Remove ${file.name}, ${formatFileSize(file.size)}`}
          onClick={status === 'error' ? onRetry : undefined}
          clickLabel={`Retry upload of ${file.name}`}
        />
      </Box>
    </Tooltip>
  );
}

/** Three overlapping wells — enough to say "images and a spreadsheet". */
function SummaryThumbs({ files }: { readonly files: readonly File[] }) {
  return (
    <HStack spacing={0} flexShrink={0}>
      {files.map((file, index) => (
        <SummaryThumb
          key={`${file.name}-${file.size}-${index}`}
          file={file}
          isStacked={index > 0}
        />
      ))}
    </HStack>
  );
}

function SummaryThumb({ file, isStacked }: { readonly file: File; readonly isStacked: boolean }) {
  const previewUrl = useImagePreview(file);

  return (
    <Box
      w="18px"
      h="18px"
      borderRadius="sm"
      bg="bg.subtle"
      border="1px solid"
      borderColor="border.default"
      overflow="hidden"
      display="flex"
      alignItems="center"
      justifyContent="center"
      flexShrink={0}
      ml={isStacked ? '-6px' : 0}
    >
      <AttachmentWell file={file} status="ready" previewUrl={previewUrl} />
    </Box>
  );
}

export function ComposerAttachmentTray({
  files,
  onRemove,
  statuses,
  onRetry,
}: ComposerAttachmentTrayProps) {
  const [isExpanded, setIsExpanded] = useState(false);

  const statusAt = (index: number): AttachmentStatus => statuses?.[index] ?? 'ready';
  const uploadingCount = files.filter((_, i) => statusAt(i) === 'uploading').length;
  const hasError = files.some((_, i) => statusAt(i) === 'error');

  // An error the user can't see is an error they can't retry, so a failure
  // always forces the row open regardless of how many files are staged.
  const isCollapsed = files.length > COLLAPSE_THRESHOLD && !isExpanded && !hasError;

  if (files.length === 0) return null;

  if (isCollapsed) {
    return (
      <HStack
        as="button"
        type="button"
        onClick={() => setIsExpanded(true)}
        aria-label={`Review ${files.length} attached files`}
        spacing={2}
        mb={2}
        h="28px"
        px={1.5}
        w="full"
        borderRadius="md"
        _hover={{ bg: 'bg.hover' }}
        _focusVisible={{ boxShadow: 'outline', outline: 'none' }}
        transition="all 0.15s"
      >
        <SummaryThumbs files={files.slice(0, 3)} />
        <Text fontSize="xs" fontWeight="medium" color="text.primary">
          {files.length} files attached
        </Text>
        {uploadingCount > 0 && (
          <Text fontSize="xs" color="text.muted">
            {uploadingCount} uploading
          </Text>
        )}
        <Box flex={1} />
        <Text fontSize="xs" fontWeight="medium" color="brand.primary">
          Review
        </Text>
      </HStack>
    );
  }

  const lead =
    files.length > COLLAPSE_THRESHOLD ? (
      <Tooltip label="Collapse attachments" placement="top">
        <Box
          as="button"
          type="button"
          aria-label="Collapse attachments"
          onClick={() => setIsExpanded(false)}
          flexShrink={0}
          display="inline-flex"
          alignItems="center"
          justifyContent="center"
          w="28px"
          h="28px"
          borderRadius="md"
          color="text.muted"
          _hover={{ bg: 'bg.hover', color: 'text.primary' }}
          _focusVisible={{ boxShadow: 'outline', outline: 'none' }}
          transition="all 0.15s"
        >
          <MaterialIcon name="unfold_less" boxSize={4} />
        </Box>
      </Tooltip>
    ) : undefined;

  return (
    <ComposerScrollRow
      ariaLabel="Attached files"
      lead={lead}
      overflowNoun={['file', 'files']}
      measureKey={files}
    >
      {files.map((file, index) => (
        <AttachmentChip
          key={`${file.name}-${file.size}-${index}`}
          file={file}
          status={statusAt(index)}
          onRemove={() => onRemove(index)}
          onRetry={onRetry ? () => onRetry(index) : undefined}
        />
      ))}
    </ComposerScrollRow>
  );
}
